// src/components/admin/act/report/ACTSubcategoryBreakdown.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebaseConfig";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";

/**
 * ACT Subcategory Breakdown (SAT-style layout)
 * - UI matches the SAT SubcategoryBreakdown (same inline styles + "battery" RAW% UI).
 * - Logic stays the same as your existing ACT breakdown:
 *   - Builds category-level RAW% from attempt answers vs correct answers
 *   - Computes Rx hours using missed% rounded up to nearest 10% (multiplied by subject multiplier)
 *   - Science hides when scienceSkipped is true (or no science data)
 */

// ---------------------- Helpers (logic) ----------------------
const splitCorrect = (val) =>
  String(val || "")
    .split(/[,\s/|]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);

const getSubjectFromQid = (qid) => {
  const s = String(qid || "");
  const prefix = s.split("_")[0];
  return prefix || "Uncategorized";
};

function pct(c, t) {
  if (!t || t === 0) return null;
  return Math.round((c / t) * 100);
}

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/**
 * Rx hours rule:
 * - Hours are based on missed % rounded up to the nearest 10%.
 * - Then multiplied by a subject multiplier (Math tends to be lighter).
 *
 * hours = MULT(subject) * ceil((100 - rawPct) / 10)
 */
const RX_MULTIPLIER_BY_SUBJECT = {
  English: 2,
  Reading: 2,
  Science: 2,
  Math: 1,
};

// Desired ordering (everything else will appear after)
const CATEGORY_ORDER = {
  English: [
    "Production of Writing",
    "Knowledge of Language",
    "Conventions of Standard English",
  ],
  Math: [
    "Number & Quantity",
    "Algebra",
    "Functions",
    "Geometry",
    "Statistics & Probability",
    "Integrating Essential Skills",
    "Integrating Essential Skills (IES)",
  ],
  Reading: [
    "Key Ideas & Details",
    "Craft & Structure",
    "Integration of Knowledge & Ideas",
  ],
  Science: [
    "Interpretation of Data",
    "Scientific Investigation",
    "Evaluation of Models, Inferences & Experimental Results",
  ],
};

function sortByPreferredOrder(subject, categories) {
  const order = CATEGORY_ORDER[subject] || [];
  const idx = new Map(order.map((name, i) => [String(name), i]));
  return [...categories].sort((a, b) => {
    const ia = idx.has(a) ? idx.get(a) : 9999;
    const ib = idx.has(b) ? idx.get(b) : 9999;
    if (ia !== ib) return ia - ib;
    return String(a).localeCompare(String(b));
  });
}

function rxHoursFor(subject, rawPct) {
  if (rawPct == null) return null;
  const mult = RX_MULTIPLIER_BY_SUBJECT[subject] ?? 2;
  const missed = clamp(100 - rawPct, 0, 100);
  const buckets = Math.ceil(missed / 10);
  return mult * buckets;
}

function formatDateLabel(value) {
  if (!value) return "";
  try {
    // Firestore Timestamp
    if (typeof value?.toDate === "function") {
      const d = value.toDate();
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    }
    // number (ms)
    if (typeof value === "number") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
    }
    // string
    if (typeof value === "string") {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
    }
  } catch {
    // ignore
  }
  return "";
}

// ---------------------- SAT-style inline UI (copied) ----------------------
const styles = {
  wrapper: {
    background: "#f9fafb",
    borderRadius: 16,
    padding: 16,
    border: "1px solid #e5e7eb",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 12,
  },
  title: { fontSize: 15, fontWeight: 700, color: "#111827", margin: 0 },
  helper: { fontSize: 12, color: "#6b7280" },
  
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr", // <--- Forces 2x2 layout
    gap: 16,
  },
  subjectCard: {
    background: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  subjectHeader: {
    padding: "10px 14px",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 700,
    fontSize: 14,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#111827",
    background: "#e5e7eb", // <--- Gray (Subject Title)
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  th: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    textAlign: "left",
    color: "#6b7280",
    padding: "8px 10px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff", // <--- White (to separate from rows)
    whiteSpace: "nowrap",
  },
  td: {
    fontSize: 12,
    color: "#111827",
    padding: "6px 10px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  },
  // Category row (unit)
  unitRow: {
    background: "#f9fafb", // <--- Very Light Gray (Categories)
    fontWeight: 700,
    fontSize: 13,
  },
  dash: { color: "#d1d5db" },
  totalRow: {
    fontWeight: 700,
    background: "#f3f4f6",
    borderTop: "1px solid #e5e7eb",
  },
  totalLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontSize: 11,
    color: "#4b5563",
  },
  empty: {
    fontSize: 13,
    color: "#6b7280",
    padding: "10px 0",
  },

  // accuracy "battery" UI
  accuracyCell: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    alignItems: "flex-end",
  },
  accuracyBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "1px 8px",
    borderRadius: 999,
    display: "inline-block",
  },
  accuracyBarOuter: {
    width: 72,
    height: 4,
    borderRadius: 999,
    background: "#e5e7eb",
    overflow: "hidden",
  },
  accuracyBarInner: {
    height: "100%",
    borderRadius: 999,
  },
};


function accuracyColors(percentage) {
  if (percentage == null) {
    return {
      badgeBg: "#e5e7eb",
      badgeText: "#4b5563",
      barBg: "#9ca3af",
    };
  }
  if (percentage >= 80) {
    return {
      badgeBg: "#dcfce7",
      badgeText: "#166534",
      barBg: "#22c55e",
    };
  }
  if (percentage >= 60) {
    return {
      badgeBg: "#fef9c3",
      badgeText: "#92400e",
      barBg: "#facc15",
    };
  }
  return {
    badgeBg: "#fee2e2",
    badgeText: "#b91c1c",
    barBg: "#ef4444",
  };
}

function AccuracyCell({ percentage, total }) {
  if (total === 0 || percentage == null) {
    return <span style={styles.dash}>—</span>;
  }
  const { badgeBg, badgeText, barBg } = accuracyColors(percentage);
  const width = `${percentage}%`;

  return (
    <div style={styles.accuracyCell}>
      <span
        style={{
          ...styles.accuracyBadge,
          backgroundColor: badgeBg,
          color: badgeText,
        }}
      >
        {percentage}%
      </span>
      <div style={styles.accuracyBarOuter}>
        <div
          style={{
            ...styles.accuracyBarInner,
            width,
            backgroundColor: barBg,
          }}
        />
      </div>
    </div>
  );
}

// ---------------------- Component ----------------------
export default function ACTSubcategoryBreakdown({
  studentId,
  testId,
  attemptId,
  attemptLabel = "",
}) {
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [scienceSkipped, setScienceSkipped] = useState(false);
  const [qIndex, setQIndex] = useState(new Map()); // qId -> { subject, category, correctAnswer }
  const [dateLabel, setDateLabel] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        // 1) Question metadata (category + correctAnswer)
        const qSnap = await getDocs(
          collection(db, "act_diagnostic_tests", testId, "questions")
        );
        const map = new Map();
        qSnap.forEach((d) => {
          const v = d.data() || {};
          const key = v.questionId || d.id;
          map.set(key, {
            questionId: key,
            subject: v.subject,
            category: v.category ?? v.subcategory ?? v.skill,
            correctAnswer: v.correctAnswer,
          });
        });

        // 2) Attempt doc (answers + scienceSkipped + optional date)
        let attemptAnswers = {};
        let sciSkip = false;
        let date = "";

        if (attemptId || studentId) {
          const reportRef = doc(db, "diagnostic_results", attemptId || studentId);
          const reportSnap = await getDoc(reportRef);
          if (reportSnap.exists()) {
            const d = reportSnap.data() || {};
            attemptAnswers = d.answers || {};
            sciSkip = !!d.scienceSkipped;

            // Best-effort date label (keeps UI consistent if field exists)
            date =
              formatDateLabel(d.completedAt) ||
              formatDateLabel(d.completedOn) ||
              formatDateLabel(d.completedDate) ||
              formatDateLabel(d.date) ||
              "";
          }
        }

        if (!cancelled) {
          setQIndex(map);
          setAnswers(attemptAnswers || {});
          setScienceSkipped(sciSkip);
          setDateLabel(date);
        }
      } catch (e) {
        console.error("ACTSubcategoryBreakdown fetch error:", e);
        if (!cancelled) {
          setQIndex(new Map());
          setAnswers({});
          setScienceSkipped(false);
          setDateLabel("");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (!studentId || !testId) {
      setLoading(false);
      setQIndex(new Map());
      setAnswers({});
      setScienceSkipped(false);
      setDateLabel("");
      return;
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, testId, attemptId]);

  const ui = useMemo(() => {
    // subject -> category -> { total, correct }
    const subjectMap = new Map();

    for (const [qId, qDataRaw] of qIndex.entries()) {
      const qData = qDataRaw || {};
      const subject = qData.subject || getSubjectFromQid(qId);
      if (scienceSkipped && subject === "Science") continue;

      const category = String(qData.category || "Uncategorized").trim() || "Uncategorized";
      if (String(category).toLowerCase() === "not scored") continue;

      const ansRaw = answers?.[qId] ?? "";
      const studentAns = String(ansRaw || "").trim().toUpperCase();
      const correctArr = splitCorrect(qData.correctAnswer);
      const isCorrect = !!studentAns && correctArr.includes(studentAns);

      if (!subjectMap.has(subject)) subjectMap.set(subject, new Map());
      const catMap = subjectMap.get(subject);

      if (!catMap.has(category)) catMap.set(category, { total: 0, correct: 0 });
      const bucket = catMap.get(category);

      bucket.total += 1;
      if (isCorrect) bucket.correct += 1;
    }

    const subjects = ["English", "Math", "Reading", "Science"];

    const subjectCards = subjects
      .map((subject) => {
        const catMap = subjectMap.get(subject) || new Map();
        const categories = sortByPreferredOrder(subject, Array.from(catMap.keys()));

        let subjectTotal = 0;
        let subjectRx = 0;

        const rows = categories.map((cat) => {
          const { total, correct } = catMap.get(cat) || { total: 0, correct: 0 };
          subjectTotal += total;

          const rawPct = pct(correct, total);
          const rx = rxHoursFor(subject, rawPct);
          if (typeof rx === "number") subjectRx += rx;

          return { category: cat, total, correct, rawPct, rx };
        });

        const shouldHide = subjectTotal === 0 && subjectRx === 0;
        if (subject === "Science" && scienceSkipped) return null;
        if (shouldHide) return null;

        return { subject, subjectRx, rows };
      })
      .filter(Boolean);

    return { subjectCards };
  }, [qIndex, answers, scienceSkipped]);

  if (loading) return <div style={styles.empty}>Loading breakdown…</div>;
  if (!ui?.subjectCards?.length) return <div style={styles.empty}>No subcategory data available.</div>;

  const helperRight = (() => {
    const parts = [];
    if (attemptLabel) parts.push(attemptLabel);
    if (dateLabel) parts.push(dateLabel);
    return parts.length ? ` • ${parts.join(" • ")}` : "";
  })();

  return (
    <div style={styles.wrapper}>
      <div style={styles.cardHeader}>
        <h3 style={styles.title}>Subcategory Breakdown</h3>
        <span style={styles.helper}>
          RAW% and suggested Rx hours{helperRight}
        </span>
      </div>

      <div style={styles.grid}>
        {ui.subjectCards.map((card) => {
          const subjectHours = card.subjectRx || 0;

          return (
            <div key={card.subject} style={styles.subjectCard}>
              <div style={styles.subjectHeader}>{card.subject}</div>

              <table style={styles.table}>
                <colgroup>
                  <col style={{ width: "60%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={styles.th}>Category</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>RAW%</th>
                    <th style={{ ...styles.th, textAlign: "right" }}>Rx (hrs)</th>
                  </tr>
                </thead>
                <tbody>
                  {card.rows.map((r) => (
                    <tr key={r.category} style={styles.unitRow}>
                      <td style={styles.td}>{r.category}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        <AccuracyCell percentage={r.rawPct} total={r.total} />
                      </td>
                      <td style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>
                        {typeof r.rx === "number" && r.rx > 0 ? r.rx : <span style={styles.dash}>—</span>}
                      </td>
                    </tr>
                  ))}

                  <tr style={styles.totalRow}>
                    <td style={styles.td}>
                      <span style={styles.totalLabel}>TOTAL</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", color: "#6b7280" }}>
                      <span style={styles.dash}>—</span>
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>
                      {subjectHours > 0 ? `${subjectHours} hrs` : "0 hrs"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
